// KampfrichterDisplayPage.jsx — Informationsbildschirm Kampfrichtereinteilung
// (25.09.2026)
//
// Für einen externen Bildschirm in der Halle: jeder eingeteilte Kampfrichter
// sieht, an welcher Kampffläche er steht. Zwei Ansichten wechseln sich ab:
//   1. Namen A–Z, neben jedem Namen die Fläche in ihrer Farbe
//   2. eine Spalte je Kampffläche mit den Namen darunter
// Passt eine Ansicht nicht auf einen Schirm, wird sie auf mehrere Folien
// verteilt; alle Folien laufen nacheinander durch.
//
// Daten: GET /api/schedule/tournament/:id/assignments (dieselbe Antwort wie
// die Verwaltung, Reiter Personal → Einteilung), alle 30 Sekunden neu.
// Der Bildschirm braucht die Anmeldung als Admin in diesem Browser — die
// Einteilung enthält Namen und ist darum nicht öffentlich.
//
// Adresszusätze:
//   ?wechsel=20        Sekunden je Folie (5 bis 300, Standard 20)
//   ?ansicht=namen     nur die Namensliste   ·   ?ansicht=flaechen  nur Spalten
//
// Gestaltung: styles/leinwand.css (.lw) plus KampfrichterDisplayPage.css.
// Keine Emoji (siehe CLAUDE.md, Leinwände).
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import '../styles/leinwand.css';
import './KampfrichterDisplayPage.css';

const NEU_LADEN_MS = 30000;
// Die Anmeldung gilt nur Stunden, ein Turniertag ist länger. Läuft das Token
// in weniger als 30 Minuten ab, verlängert der Bildschirm es selbst
// (POST /api/auth/refresh, +2 h) — solange er läuft, bleibt er angemeldet.
const VERLAENGERN_UNTER_MS = 30 * 60 * 1000;

/** Ablaufzeit aus dem Token lesen (ms), ohne es zu prüfen — das tut der Server. */
function ablaufMs(token) {
  try {
    const teil = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(teil)).exp * 1000;
  } catch (e) {
    return 0;
  }
}

async function verlaengernWennNoetig(token) {
  const rest = ablaufMs(token) - Date.now();
  if (rest <= 0 || rest > VERLAENGERN_UNTER_MS) return token;
  try {
    const r = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    const d = await r.json().catch(() => ({}));
    if (r.ok && d.token) {
      localStorage.setItem('token', d.token);
      return d.token;
    }
  } catch (e) { /* nächster Durchlauf versucht es wieder */ }
  return token;
}
const NAMEN_JE_FOLIE = 48;   // 4 Spalten × 12 Zeilen, lesbar bis 720p
const FLAECHEN_JE_FOLIE = 6;

const ROLLEN = {
  hauptkampfrichter: 'Hauptkampfrichter',
  seitenkampfrichter: 'Seitenkampfrichter',
  punktrichter: 'Punktrichter',
  zeitnehmer: 'Zeitnehmer',
};

/** "2026-09-25T09:00" → "09:00" — Text, keine Zeitzonenrechnung */
const uhrzeit = (s) => (s ? s.slice(11, 16) : '');
const zeitraum = (z) => (z.assignment_start ? `${uhrzeit(z.assignment_start)}–${uhrzeit(z.assignment_end)}` : '');

function teilen(liste, groesse) {
  const out = [];
  for (let i = 0; i < liste.length; i += groesse) out.push(liste.slice(i, i + groesse));
  return out;
}

export default function KampfrichterDisplayPage() {
  const { turnierId } = useParams();
  const [suche] = useSearchParams();
  const wechselS = Math.min(300, Math.max(5, Number(suche.get('wechsel')) || 20));
  const nurAnsicht = suche.get('ansicht'); // 'namen' | 'flaechen' | null

  const [daten, setDaten] = useState(null);
  const [fehler, setFehler] = useState('');
  const [folie, setFolie] = useState(0);
  const [jetzt, setJetzt] = useState(() => new Date());
  const [vollbild, setVollbild] = useState(false);

  // ── Seite füllt den Bildschirm, nichts scrollt weg ─────────────────
  useEffect(() => {
    const vorher = { html: document.documentElement.style.overflow, body: document.body.style.overflow };
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = vorher.html;
      document.body.style.overflow = vorher.body;
    };
  }, []);

  // Chat-Widget und alles andere außerhalb der App ausblenden
  useEffect(() => {
    const verstecken = () => {
      document.body.querySelectorAll('body > *:not(#root)').forEach(el => {
        if (!['SCRIPT', 'STYLE', 'LINK', 'META'].includes(el.tagName)) {
          el.style.setProperty('display', 'none', 'important');
        }
      });
    };
    verstecken();
    const beobachter = new MutationObserver(verstecken);
    beobachter.observe(document.body, { childList: true });
    return () => beobachter.disconnect();
  }, []);

  useEffect(() => {
    const merken = () => setVollbild(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', merken);
    return () => document.removeEventListener('fullscreenchange', merken);
  }, []);

  // ── Daten holen ─────────────────────────────────────────────────────
  const laden = useCallback(async () => {
    let token = localStorage.getItem('token');
    // Ohne Token gar nicht erst fragen: ein 401 löst in index.js die globale
    // Abmeldung aus und springt auf die Startseite — auf einem Hallenschirm
    // stünde dann die Werbeseite statt eines Hinweises.
    if (!token) {
      setFehler('Bitte in diesem Browser als Administrator anmelden, der Bildschirm lädt dann von selbst.');
      return;
    }
    token = await verlaengernWennNoetig(token);
    try {
      const r = await fetch(`/api/schedule/tournament/${turnierId}/assignments`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (r.status === 401) { setFehler('Anmeldung abgelaufen. Bitte in diesem Browser neu als Administrator anmelden, der Bildschirm lädt dann von selbst weiter.'); return; }
      if (r.status === 403) { setFehler('Dieses Konto darf die Einteilung nicht sehen. Bitte als Administrator anmelden.'); return; }
      if (r.status === 404) { setFehler('Turnier nicht gefunden.'); return; }
      const d = await r.json();
      if (!r.ok || !d?.data) { setFehler(d?.message || 'Einteilung konnte nicht geladen werden.'); return; }
      setDaten(d.data);
      setFehler('');
    } catch (e) {
      // Netz weg: letzten Stand stehen lassen, beim nächsten Durchlauf erneut.
      setFehler('Keine Verbindung zum Server.');
    }
  }, [turnierId]);

  useEffect(() => {
    laden();
    const id = setInterval(laden, NEU_LADEN_MS);
    return () => clearInterval(id);
  }, [laden]);

  useEffect(() => {
    const id = setInterval(() => setJetzt(new Date()), 15000);
    return () => clearInterval(id);
  }, []);

  // ── Aufbereiten ─────────────────────────────────────────────────────
  // Vergangene Zeitfenster fallen weg. Je Person zählt zuerst, was gerade
  // gilt, sonst das nächste.
  const personen = useMemo(() => {
    if (!daten) return [];
    const offen = daten.zuteilungen.filter(z => !z.vorbei);
    const jePerson = new Map();
    offen.forEach(z => {
      const k = z.kampfrichter_id ? `k${z.kampfrichter_id}` : `u${z.user_id}`;
      if (!jePerson.has(k)) jePerson.set(k, []);
      jePerson.get(k).push(z);
    });
    return [...jePerson.values()].map(liste => {
      liste.sort((a, b) => (b.jetzt - a.jetzt) || String(a.assignment_start).localeCompare(String(b.assignment_start)));
      return { name: liste[0].referee_name || 'unbekannt', haupt: liste[0], weitere: liste.slice(1) };
    }).sort((a, b) => a.name.localeCompare(b.name, 'de'));
  }, [daten]);

  const spalten = useMemo(() => {
    if (!daten) return [];
    const offen = daten.zuteilungen.filter(z => !z.vorbei);
    const spalten = daten.flaechen.map(f => ({
      ...f,
      liste: offen
        .filter(z => z.kampfflaeche_id === f.kampfflaeche_id)
        .sort((a, b) => (b.jetzt - a.jetzt)
          || String(a.assignment_start).localeCompare(String(b.assignment_start))
          || String(a.referee_name).localeCompare(String(b.referee_name), 'de')),
    }));
    // Doppelt angelegte Flächen (gleicher Name, lokal Turnier 3): die leere
    // Dublette ausblenden, sonst stehen zwei "Matte 1" nebeneinander.
    const besetzt = new Set(spalten.filter(f => f.liste.length).map(f => f.name));
    const gesehen = new Set();
    return spalten.filter(f => {
      if (!f.liste.length && (besetzt.has(f.name) || gesehen.has(f.name))) return false;
      gesehen.add(f.name);
      return true;
    });
  }, [daten]);

  const folien = useMemo(() => {
    const f = [];
    if (nurAnsicht !== 'flaechen') {
      const seiten = teilen(personen, NAMEN_JE_FOLIE);
      seiten.forEach((s, i) => f.push({ art: 'namen', liste: s, teil: i + 1, von: seiten.length }));
    }
    if (nurAnsicht !== 'namen') {
      const seiten = teilen(spalten, FLAECHEN_JE_FOLIE);
      seiten.forEach((s, i) => f.push({ art: 'flaechen', liste: s, teil: i + 1, von: seiten.length }));
    }
    return f;
  }, [personen, spalten, nurAnsicht]);

  // Folienwechsel
  useEffect(() => {
    if (folien.length < 2) { setFolie(0); return undefined; }
    const id = setInterval(() => setFolie(n => (n + 1) % folien.length), wechselS * 1000);
    return () => clearInterval(id);
  }, [folien.length, wechselS]);

  const aktuell = folien[Math.min(folie, Math.max(0, folien.length - 1))];

  const vollbildUmschalten = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else document.documentElement.requestFullscreen?.({ navigationUI: 'hide' }).catch(() => {});
  };

  const titel = !aktuell ? 'Kampfrichter'
    : aktuell.art === 'namen' ? 'Kampfrichter A–Z' : 'Kampfrichter je Fläche';

  return (
    <div className="lw kd">
      <header className="lw-kopf">
        <div className="lw-kopf-links">
          <div className="lw-kopf-turnier">{daten?.turnier?.name || ''}</div>
          <div className="lw-kopf-kat">Kampfrichtereinteilung</div>
        </div>
        <div className="lw-kopf-titel">{titel}</div>
        <div className="lw-kopf-rechts">
          <div className="lw-kopf-status">
            {jetzt.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
          </div>
        </div>
      </header>

      <main className="lw-buehne kd-buehne">
        {fehler && !daten ? (
          <div className="lw-bereit-text kd-meldung">{fehler}</div>
        ) : !daten ? (
          <div className="lw-bereit-text">Lade Einteilung …</div>
        ) : folien.length === 0 || (personen.length === 0) ? (
          <div className="kd-leer">
            <div className="lw-kanji">審判</div>
            <div className="lw-bereit-text">Noch keine Kampfrichter eingeteilt</div>
          </div>
        ) : aktuell.art === 'namen' ? (
          <NamenFolie key={`n${aktuell.teil}`} liste={aktuell.liste} />
        ) : (
          <FlaechenFolie key={`f${aktuell.teil}`} liste={aktuell.liste} />
        )}
      </main>

      <footer className="lw-fuss">
        <div className="kd-fuss-links">
          {aktuell && aktuell.von > 1 && (
            <span className="lw-fuss-lbl">Seite {aktuell.teil} von {aktuell.von}</span>
          )}
          {folien.length > 1 && (
            <span className="kd-punkte" aria-hidden="true">
              {folien.map((_, i) => <span key={i} className={`kd-punkt${i === folie ? ' an' : ''}`} />)}
            </span>
          )}
        </div>
        <div className="kd-fuss-rechts">
          {fehler && daten && <span className="kd-warnung">{fehler} Angezeigt wird der letzte Stand.</span>}
          <span className="lw-fuss-lbl">{personen.length} eingeteilt</span>
          {!vollbild && (
            <button className="lw-vollbild" onClick={vollbildUmschalten}>Vollbild</button>
          )}
        </div>
      </footer>
    </div>
  );
}

function Flaeche({ z }) {
  return (
    <span className="kd-flaeche" style={{ '--kd-farbe': z.flaeche_farbe || '#8B0000' }}>
      {z.flaeche_name || `Ring ${z.ring_number}`}
    </span>
  );
}

function NamenFolie({ liste }) {
  // Spaltenzahl nach Menge: wenige Namen groß, viele kleiner.
  const spalten = liste.length <= 8 ? 1 : liste.length <= 20 ? 2 : liste.length <= 36 ? 3 : 4;
  const zeilen = Math.ceil(liste.length / spalten);
  return (
    <div className="kd-namen kd-einblenden" style={{ '--kd-spalten': spalten, '--kd-zeilen': zeilen }}>
      {liste.map(p => (
        <div key={`${p.haupt.assignment_id}`} className="kd-zeile">
          <div className="kd-person">
            <span className="kd-name">{p.name}</span>
            <span className="kd-rolle">
              {ROLLEN[p.haupt.role] || p.haupt.role}
              {p.haupt.assignment_start && ` · ${p.haupt.jetzt ? 'bis' : 'ab'} ${uhrzeit(p.haupt.jetzt ? p.haupt.assignment_end : p.haupt.assignment_start)} Uhr`}
            </span>
            {/* Der nächste Wechsel in eigener Zeile — hängt er an der Rolle,
                schneidet ihn eine schmale Spalte als Erstes ab. */}
            {p.weitere.length > 0 && (
              <span className="kd-danach">
                danach {p.weitere.map(w => `${w.flaeche_name || `Ring ${w.ring_number}`} ab ${uhrzeit(w.assignment_start)}`).join(', ')}
              </span>
            )}
          </div>
          <Flaeche z={p.haupt} />
        </div>
      ))}
    </div>
  );
}

function FlaechenFolie({ liste }) {
  // Je Fläche groß, wer JETZT dort steht. Spätere Schichten kommen in eine
  // kompakte Zeile darunter — mit ihnen als volle Einträge lief eine Spalte
  // bei zwei Schichten je Person unten aus dem Bild (gemessen: 8 Einträge bei
  // 24 Kampfrichtern auf 5 Flächen). Steht noch niemand, weil der Tag erst
  // beginnt, zeigt die Spalte die kommenden Einträge als Liste.
  const spalten = liste.map(f => {
    const jetzt = f.liste.filter(z => z.jetzt);
    const spaeter = f.liste.filter(z => !z.jetzt);
    return jetzt.length ? { ...f, gross: jetzt, klein: spaeter } : { ...f, gross: spaeter, klein: [] };
  });
  // Schriftgröße nach der vollsten Spalte, damit keine unten herausläuft.
  const max = Math.max(1, ...spalten.map(f => f.gross.length));
  return (
    <div className="kd-spalten kd-einblenden" style={{ '--kd-spalten': liste.length, '--kd-max': max }}>
      {spalten.map(f => (
        <section key={f.kampfflaeche_id} className="kd-spalte" style={{ '--kd-farbe': f.farbe || '#8B0000' }}>
          <header className="kd-spalte-kopf">{f.name}</header>
          {f.gross.length === 0 ? (
            <div className="kd-spalte-leer">nicht besetzt</div>
          ) : (
            <ul className="kd-spalte-liste">
              {f.gross.map(z => (
                <li key={z.assignment_id} className={`kd-spalte-eintrag${z.jetzt ? '' : ' spaeter'}`}>
                  <span className="kd-name">{z.referee_name || 'unbekannt'}</span>
                  <span className="kd-rolle">
                    {ROLLEN[z.role] || z.role}{zeitraum(z) && ` · ${zeitraum(z)}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {f.klein.length > 0 && (
            <div className="kd-spalte-spaeter">
              <span className="kd-spalte-spaeter-lbl">später</span>{' '}
              {f.klein.map(z => `${z.referee_name || 'unbekannt'} ab ${uhrzeit(z.assignment_start)}`).join(' · ')}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}
